// ──────────────────────────────────────────────────────────────────────
// Batch 5 D48 BUG-09 — Reviews Queue page.
// Shows all tasks awaiting the current user's review. Each row carries
// the latest submission metadata + inline Open / Approve / Request
// Changes actions. Approve fires the global 'stellar-task-changed'
// event (BUG-07) so all downstream surfaces refresh.
// ──────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { reviewsAPI } from '../lib/api';
import { useLiveInvalidate } from '../lib/useLiveData';
import { ClipboardCheck, Loader2, Clock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { TaskDetailSheet } from '../components/TaskDetailSheet';
import { ReviewAuditDialog } from '../components/ReviewAuditDialog';
import { ShieldCheck } from 'lucide-react';

const SCOPES = [
  { id: 'mine', label: 'Mine' },
  { id: 'team', label: 'All team reviews' },
];

export default function ReviewsPage() {
  const { currentWorkspace, workspaceRole } = useWorkspace();
  const { user } = useAuth();
  const [scope, setScope] = useState('mine');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ reviews: [], count: 0 });
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const canSeeTeam = ['owner', 'admin', 'manager'].includes(workspaceRole) || user?.role === 'super_admin';
  const isOwner = workspaceRole === 'owner' || user?.role === 'super_admin';

  const refresh = useCallback(({ silent = false } = {}) => {
    if (!currentWorkspace?.id) return;
    // D88+++ (2026-06-04): `silent` skips the page-level setLoading flip so
    // background WS pushes and the 15s poll don't remount the reviews list
    // / re-flash the skeleton on every teammate action.
    if (!silent) setLoading(true);
    reviewsAPI.queue(currentWorkspace.id, scope)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => { if (!silent) setLoading(false); });
  }, [currentWorkspace?.id, scope]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const onChanged = () => refresh({ silent: true });
    window.addEventListener('stellar-task-changed', onChanged);
    const pollId = setInterval(onChanged, 15_000);
    return () => {
      window.removeEventListener('stellar-task-changed', onChanged);
      clearInterval(pollId);
    };
  }, [refresh]);
  // D84 — Real-time push for any review submitted/approved by teammates. Silent.
  const _silentRefresh = useCallback(() => refresh({ silent: true }), [refresh]);
  useLiveInvalidate('reviews', _silentRefresh);
  // Reviews are derived from tasks; also refresh when tasks change.
  useLiveInvalidate('tasks', _silentRefresh);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-4" data-testid="reviews-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[20px] font-display font-bold text-foreground tracking-tight flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-gold" />
            Reviews
          </h1>
          <p className="text-[12px] text-muted-foreground/60 mt-0.5">
            Tasks awaiting your sign-off, oldest submission first.
          </p>
        </div>
        <div className="flex items-center gap-2">
        {isOwner && (
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setAuditOpen(true)} data-testid="open-review-audit-btn">
            <ShieldCheck className="h-3.5 w-3.5 mr-1 text-gold" /> Audit
          </Button>
        )}
        {canSeeTeam && (
          <div className="flex items-center rounded-lg border border-border/20 bg-foreground/[0.02] p-0.5" data-testid="reviews-scope-toggle">
            {SCOPES.map(s => (
              <button
                key={s.id}
                onClick={() => setScope(s.id)}
                className={`px-3 py-1 rounded-md text-[12px] font-medium transition-colors ${scope === s.id ? 'bg-gold/15 text-gold' : 'text-muted-foreground/50 hover:text-foreground'}`}
                data-testid={`reviews-scope-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[12px] text-muted-foreground/50" data-testid="reviews-loading">
          <Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Loading reviews…
        </div>
      ) : data.count === 0 ? (
        <Card className="bg-card/60 backdrop-blur" data-testid="reviews-empty">
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-400/70 mx-auto mb-2" />
            <p className="text-[14px] font-medium text-foreground">All caught up</p>
            <p className="text-[12px] text-muted-foreground/50 mt-1">No tasks awaiting your review right now.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="reviews-list">
          {data.reviews.map((r) => {
            const ageColor = (r.days_waiting ?? 0) >= 2 ? 'text-red-300' : (r.days_waiting ?? 0) >= 1 ? 'text-amber-300' : 'text-muted-foreground/60';
            return (
              <Card
                key={r.task_id}
                className="bg-card/60 backdrop-blur hover:border-gold/30 transition-colors cursor-pointer"
                onClick={() => setSelectedTaskId(r.task_id)}
                data-testid={`reviews-row-${r.task_id}`}
              >
                <CardContent className="p-3.5 flex items-center gap-3">
                  <Badge variant="outline" className="text-[10px] font-mono shrink-0 bg-amber-500/10 text-amber-300 border-amber-500/30" data-testid={`reviews-version-${r.task_id}`}>
                    V{r.latest_version ?? '?'}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-foreground truncate">{r.title}</div>
                    <div className="text-[11px] text-muted-foreground/60 mt-0.5 flex items-center gap-2 flex-wrap">
                      {r.client_name && <span>{r.client_name}</span>}
                      {r.submitter && <span>· by {r.submitter.name}</span>}
                      <span className={`inline-flex items-center gap-1 ${ageColor}`}>
                        <Clock className="h-3 w-3" />
                        {r.days_waiting === 0 ? 'today' : r.days_waiting === 1 ? '1 day' : `${r.days_waiting} days`}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gold hover:bg-gold/10"
                    onClick={(e) => { e.stopPropagation(); setSelectedTaskId(r.task_id); }}
                    data-testid={`reviews-open-${r.task_id}`}
                  >
                    Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {auditOpen && (
        <ReviewAuditDialog open={auditOpen} onOpenChange={setAuditOpen} workspaceId={currentWorkspace?.id} />
      )}

      {selectedTaskId && (
        <TaskDetailSheet
          taskId={selectedTaskId}
          open={!!selectedTaskId}
          onOpenChange={(o) => { if (!o) setSelectedTaskId(null); }}
          onUpdate={() => { refresh(); toast.success('Task updated'); }}
        />
      )}
    </div>
  );
}
